// CHEMIN COMPLET : /src/api/digiscore-api.js

import getInstance from './httpClient'
import { getEnterpriseData } from './cbe-api'
import { uniqBy, uniq } from 'lodash'

const client = getInstance(process.env.REACT_APP_API_BASE_URL)

async function getInProgressSurveyByBce(bce, email, token) {
  const params = new URLSearchParams()
  params.set('bce', String(bce || '').trim())
  if (email) params.set('email', String(email).trim())

  const config = {
    method: 'get',
    url: `/surveySession/in-progress-by-bce?${params.toString()}`,
    headers: { Authorization: `Bearer ${token}` }
  }

  const res = await client.request(config)
  return res.data
}

async function getNaceWithSector(sectors, token) {
  let filter = {
    Code: {
      '$in': sectors
    }
  }

  const config = {
    method: 'get',
    url: `/nace?filter=${encodeURI(JSON.stringify(filter))}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return uniqBy(response.data, 'sectorCode')
  } catch (error) {
    throw error
  }
}

async function getSurveySessionByUser(user, token, options = {}) {
  const roles = user['https://digitalwallonia.be/role'].map(role => role.split('_').at(-1))
  const email = user['email'].split('_').at(-1)

  const params = new URLSearchParams()
  params.set('roles', JSON.stringify(roles))
  params.set('email', email)

  if (options.limit) params.set('limit', String(options.limit))
  if (options.cursor) params.set('cursor', String(options.cursor))
  if (options.filter) {
    params.set('filter', JSON.stringify(options.filter))
  } else if (options.q) {
    params.set('q', String(options.q))
  }

  const config = {
    method: 'get',
    url: `/surveySession?${params.toString()}`,
    headers: { Authorization: `Bearer ${token}` }
  }

  const response = await client.request(config)
  return response.data
}

async function getPartners(token) {
  const config = {
    method: 'get',
    url: `/partners`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    throw error
  }
}

async function getSurveySession(id, token) {
  const config = {
    method: 'get',
    url: `/surveySession/${id}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    throw error
  }
}

async function updateSurveySession(id, data, token) {
  const config = {
    method: 'patch',
    url: `/surveySession/${id}`,
    data,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

async function setSurveySessionDraft(id, draft, token) {
  try {
    return await updateSurveySession(id, { Draft: draft }, token)
  } catch (error) {
    console.error(error)
    throw error
  }
}

async function getReferenceByNamespaceCategoryId(Namespace, Dimension, Category, token) {
  let filter = {
    Namespace,
    Dimension,
    Category
  }

  const config = {
    method: 'get',
    url: `/reference?filter=${encodeURIComponent(JSON.stringify(filter))}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    throw error
  }
}

async function getReferences(token) {
  const config = {
    method: 'get',
    url: `/reference`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    throw error
  }
}

async function createSurveySession(data, token) {
  const config = {
    method: 'post',
    url: `/surveySession`,
    data,
    headers: { Authorization: `Bearer ${token}` }
  }
  const response = await client.request(config)
  return response.data
}

async function createSurvey(data, token) {
  const config = {
    method: 'put',
    url: `/surveySession/createSurvey`,
    data: data,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

async function finishSurvey(login, controlCode, namespace, token) {
  const config = {
    method: 'put',
    url: '/surveySession/finishSurvey',
    data: {
      login: login,
      ct: controlCode,
      namespace: namespace
    },
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

async function updateSector(bce, sectorCode, sessionId, token) {
  const config = {
    method: 'put',
    url: '/YEnterpriseSectorUser/sector',
    data: {
      bce: bce,
      sector: sectorCode,
      sessionId: sessionId
    },
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

async function updateShareWith(sessionId, sharedWith, token) {
  const config = {
    method: 'put',
    url: '/surveySession/sharedWith',
    data: {
      sessionId: sessionId,
      sharedWith: sharedWith
    },
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    const response = await client.request(config)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

/*
  🔧 FIX MAJEUR ICI :
  Certaines entreprises renvoient un autre champ que "code".
  On corrige en prenant tous les champs possibles.
*/
async function getSectorsByBce(bce, token) {
  try {
    let sectorArray = []
    const response = await getEnterpriseData(bce, token)

    if (response?.content?.enterprise) {
      const ent = response.content.enterprise

      if (ent?.activities?.activitiesByNace) {
        for (let i = 0; i < ent.activities.activitiesByNace.length; i++) {
          const nac = ent.activities.activitiesByNace[i]

          const code =
            nac.code ||
            nac.naceCode ||
            nac.codeNace ||
            nac.activityCode ||
            nac?.nace?.code ||
            null

          if (code) sectorArray.push(code)
        }

        sectorArray = uniq(sectorArray.filter(Boolean))
        return await getNaceWithSector(sectorArray, token)
      }
    }

    return []
  } catch (error) {
    console.error(error)
    throw error
  }
}

async function getEthnosUrl(sessionId, sectorCode, token) {
  try {
    const config = {
      method: 'get',
      url: `/surveySession/ethnosUrl/${sessionId}/${sectorCode}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }

    const response = await client.request(config)
    return response.data
  } catch (error) {
    console.error(error)
  }
}

async function publishReport(sessionId, bce, namespace, token) {
  try {
    const reportUrl = `${window.location.origin}/report/${sessionId}`

    const config = {
      method: 'post',
      url: '/print',
      data: {
        url: reportUrl,
        token,
        bce,
        namespace,
        sessionId
      },
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 120000
    }

    const response = await client.request(config)
    return response.data.url
  } catch (error) {
    console.error('publishReport error:', error)
    throw error
  }
}

async function getSurveySessionsPage(user, token, { page = 1, pageSize = 15, q = '', statuses = [] } = {}) {
  const roles = user['https://digitalwallonia.be/role'].map(r => r.split('_').at(-1))
  const email = user.email.split('_').at(-1)

  const params = new URLSearchParams()
  params.set('roles', JSON.stringify(roles))
  params.set('email', email)
  params.set('page', String(page))
  params.set('pageSize', String(pageSize))
  if (q) params.set('q', q)
  if (statuses && statuses.length > 0) {
    params.set('statuses', JSON.stringify(statuses))
  }

  const config = {
    method: 'get',
    url: `/surveySession/page?${params.toString()}`,
    headers: { Authorization: `Bearer ${token}` }
  }

  const res = await client.request(config)
  return res.data
}

async function getSurveySession2025(sessionId, token) {
  const baseUrl = process.env.REACT_APP_API_BASE_URL

  const response = await fetch(`${baseUrl}/surveySession/${sessionId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  const text = await response.text()

  try {
    return JSON.parse(text)
  } catch (e) {
    console.error('JSON parse error:', e)
    throw e
  }
}

async function getReferences2025(token) {
  const baseUrl = process.env.REACT_APP_API_BASE_URL

  const res = await fetch(`${baseUrl}/references-2025`, {
    headers: { Authorization: `Bearer ${token}` }
  })

  if (!res.ok) throw new Error('Erreur de récupération des références 2025')

  const text = await res.text()

  try {
    return JSON.parse(text)
  } catch (e) {
    console.error('JSON parse error (refs2025):', e)
    throw e
  }
}

export {
  getSurveySession2025,
  getReferences2025,
  getInProgressSurveyByBce,
  getNaceWithSector,
  getPartners,
  getSurveySession,
  getSurveySessionByUser,
  getReferenceByNamespaceCategoryId,
  getReferences,
  createSurvey,
  finishSurvey,
  updateSector,
  updateShareWith,
  getSectorsByBce,
  getEthnosUrl,
  publishReport,
  updateSurveySession,
  setSurveySessionDraft,
  createSurveySession,
  getSurveySessionsPage
}
